import React, { useState, useEffect } from 'react';

interface ConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { key: string; value: string }) => void;
  editConfig: any | null;
}

const ConfigModal: React.FC<ConfigModalProps> = ({ isOpen, onClose, onSave, editConfig }) => {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [errors, setErrors] = useState<{ key?: string; value?: string }>({});

  useEffect(() => {
    if (editConfig) {
      setKey(editConfig.key || '');
      setValue(editConfig.value || '');
    } else {
      setKey('');
      setValue('');
    }
    setErrors({});
  }, [editConfig, isOpen]);

  const validateForm = () => {
    const newErrors: { key?: string; value?: string } = {};
    
    if (!key.trim()) {
      newErrors.key = 'Configuration key is required';
    } else if (!/^[a-zA-Z0-9_.-]+$/.test(key)) {
      newErrors.key = 'Key can only contain letters, numbers, dots, dashes, and underscores';
    }
    
    if (!value.trim()) {
      newErrors.value = 'Configuration value is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({ key: key.trim(), value: value.trim() });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">
            {editConfig ? 'Edit Configuration' : 'Add Configuration'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configuration Key *
            </label>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={!!editConfig}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.key ? 'border-red-500' : 'border-gray-300'
              } ${editConfig ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="e.g., app.name, feature.enabled"
            />
            {errors.key && <p className="text-red-500 text-sm mt-1">{errors.key}</p>}
            {editConfig && (
              <p className="text-gray-500 text-xs mt-1">Configuration key cannot be changed</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configuration Value *
            </label>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={4}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.value ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter configuration value"
            />
            {errors.value && <p className="text-red-500 text-sm mt-1">{errors.value}</p>}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition"
            >
              {editConfig ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigModal;
